import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, ColorInput, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CI_SWATCHES, CI_THEME, HEX_COLOR, type ThemeColors, contrast, ratioText, themeWarnings } from '@pst/shared';
import { ApiError, themeApi } from '../api';

const FIELDS: { key: keyof ThemeColors; label: string; hint: string }[] = [
  { key: 'primary', label: 'Hauptfarbe', hint: 'Knöpfe, aktive Reiter, Balken im Terminplan, Kopfzeilen der Excel-Dateien (CI: Pflaume)' },
  { key: 'accent', label: 'Akzentfarbe', hint: 'sparsam: heute, aktive Seite in der Navigation, Meilenstein im Zeichen (CI: Ocker)' },
  { key: 'text', label: 'Textfarbe', hint: 'Fließtext; daraus leitet PST auch die Grautöne und den Dunkelmodus ab (CI: Anthrazit)' },
  { key: 'background', label: 'Hintergrund', hint: 'Grundton der Arbeitsfläche im hellen Modus; Karten bleiben weiß (CI: Papier)' },
];

/**
 * Einstellungen › Darstellung: the few colours PST is drawn in, for everyone,
 * with the corporate design as the default. PST derives the shades, greys
 * and dark mode itself; the checks say when a choice would read badly.
 */
export function ThemePanel(): JSX.Element {
  const queryClient = useQueryClient();
  const stored = useQuery({ queryKey: ['theme'], queryFn: themeApi.get, staleTime: Infinity });
  const [draft, setDraft] = useState<ThemeColors>(CI_THEME);
  useEffect(() => {
    if (stored.data) setDraft({ primary: stored.data.primary, accent: stored.data.accent, text: stored.data.text, background: stored.data.background });
  }, [stored.data]);
  const valid = FIELDS.every((f) => HEX_COLOR.test(draft[f.key]));
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['theme'] });
  const save = useMutation({ mutationFn: () => themeApi.save(draft), onSuccess: refresh });
  const reset = useMutation({ mutationFn: themeApi.reset, onSuccess: refresh });
  const warnings = valid ? themeWarnings(draft) : [];
  const unchanged = stored.data !== undefined && FIELDS.every((f) => stored.data[f.key].toUpperCase() === draft[f.key].toUpperCase());
  const error = save.error ?? reset.error;

  return (
    <Stack gap="md" maw={880}>
      <Text size="sm" c="dimmed">
        Die Farben gelten für alle Nutzenden und für die Excel-Dateien, die PST erzeugt. Vorgabe ist das Corporate Design des Büros (Entwurf V01 vom 30.09.2026).
        Abtönungen, Grautöne und den Dunkelmodus leitet PST selbst ab. Rot für den kritischen Pfad bleibt, weil es etwas bedeutet.
      </Text>
      <Group gap="xs">
        <Badge variant="light" color={stored.data?.custom ? 'gray' : 'brand'}>{stored.data?.custom ? 'eigene Farben' : 'Corporate Design'}</Badge>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {FIELDS.map((f) => (
          <ColorInput
            key={f.key}
            label={f.label}
            description={f.hint}
            format="hex"
            value={draft[f.key]}
            onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v.toUpperCase() }))}
            swatches={CI_SWATCHES.map((s) => s.hex)}
            swatchesPerRow={7}
            error={HEX_COLOR.test(draft[f.key]) ? undefined : 'als #RRGGBB'}
          />
        ))}
      </SimpleGrid>

      {valid && (
        <Paper withBorder p="md" radius="md" style={{ background: draft.background }}>
          <Stack gap="xs">
            <Text size="xs" fw={600} style={{ color: draft.primary, letterSpacing: '0.06em' }}>VORSCHAU</Text>
            <Title order={4} style={{ color: draft.primary }}>Wohnhaus Lindenstraße</Title>
            <Text size="sm" style={{ color: draft.text }}>Fließtext in der Textfarbe. Leistungsphase 3, Entwurfsplanung, bis 12.11.2026.</Text>
            <Group gap="xs">
              <span style={{ background: draft.primary, color: '#FFFFFF', padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500 }}>Knopf</span>
              <span style={{ background: draft.accent, color: draft.text, padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>heute</span>
              <Text size="xs" style={{ color: draft.text }}>Kontrast Text {ratioText(contrast(draft.text, draft.background))} · Knopf {ratioText(contrast('#FFFFFF', draft.primary))}</Text>
            </Group>
          </Stack>
        </Paper>
      )}
      {warnings.length > 0 && <Alert color="yellow" py={6}>{warnings.map((w) => <Text key={w} size="sm">{w}</Text>)}</Alert>}
      {error && <Alert color="red" py={6}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      <Group gap="xs">
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!stored.data || !valid || unchanged}>Farben übernehmen</Button>
        <Button variant="default" onClick={() => setDraft(CI_THEME)}>Werte der CI eintragen</Button>
        {stored.data?.custom && (
          <Button variant="subtle" color="gray" loading={reset.isPending} onClick={() => { if (window.confirm('Eigene Farben löschen und wieder das Corporate Design verwenden?')) reset.mutate(); }}>
            Auf Corporate Design zurücksetzen
          </Button>
        )}
      </Group>
    </Stack>
  );
}
